import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Client } from '@hero/schema';
import { PackageIcon, PlusIcon, UserIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { useClientApi, useClientListApi } from '@hero/api-hooks';
import { usePublishOrderStore } from '@hero/hooks-shared';
import { Input } from '../../../../ui/Input/Input';
import { Switch } from '../../../../ui/Switch/Switch';
import { Card } from '../../../../app/Card/Card';
import { ClientEditModal } from '../../../Client/Modal/ClientEditModal';
import { SectionHeader } from './SectionHeader';
import { rolesToLegacyCustomerType } from './customerType';

/**
 * Client step of the publish-order page. Two modes:
 *
 * 1. **External client** (default) — the user picks a CRM Client from
 *    the searchable list, or creates one via the shared
 *    `ClientEditModal`. The publish gate requires a selected client
 *    in this mode (no ad-hoc / walk-in path).
 * 2. **Internal Shipment** — toggle in the section header flips the
 *    layout. The Org ships its **own** goods (no external receiver),
 *    so the client picker collapses into a confirmation panel and the
 *    publish payload omits `clientId` + `receiver`.
 *
 * Receiver phone is **not** asked here — at create time `Order.sender` and
 * `Order.receiver` both mirror the picked Client (buyer Party); contact
 * is patched later when known. See `apps/backend/src/app/order/README.md`
 * § 4.1.
 */
export function ClientSection() {
  const { t } = useTranslation(['m-order', 'm-client', 'c-main']);

  const customer = usePublishOrderStore(s => s.customer);
  const isInternalShipment = usePublishOrderStore(s => s.isInternalShipment);
  const setCustomer = usePublishOrderStore(s => s.setCustomer);
  const setInternalShipment = usePublishOrderStore(s => s.setInternalShipment);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Top-3 default + full search. When the user hasn't typed anything
  // we only show the three most-recent clients so the picker stays
  // scannable even on a CRM with hundreds of entries; the moment the
  // user starts searching we open the limit back up so the search
  // results aren't capped at 3.
  const hasSearch = debouncedSearch.length > 0;
  const clientList = useClientListApi(
    { search: hasSearch ? debouncedSearch : undefined },
    { enabled: !isInternalShipment, limit: hasSearch ? 20 : 3 },
  );
  const rows = clientList.data ?? [];

  // Fallback fetch — when a `clientId` was pre-selected (CRM "New
  // order" CTA, or the user just picked one and is still in the
  // top-3 view), the row may not be in `rows` if the picked client is
  // outside the most-recent 3 list. Pull it explicitly so the chip
  // renders the right name + subtitle.
  const isSelectedInList = customer?.customerId
    ? rows.some(r => String(r._id) === customer.customerId)
    : true;
  const selectedFallback = useClientApi(
    !isInternalShipment && customer?.customerId && !isSelectedInList
      ? customer.customerId
      : undefined,
  );

  const handlePickClient = (c: Client) => {
    setCustomer({
      customerId: String(c._id),
      name: c.name,
      type: rolesToLegacyCustomerType(c.roles),
      // Derive the order-receiver phone from the client's primary
      // contact so we don't need an extra input on this page. The
      // payload builder will fall back to the first destination's
      // saved-location contact if this is empty.
      phone: c.primaryContact?.phone ?? undefined,
    });
  };

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const handleClientSaved = ({ client }: { client: Client }) => {
    toast.success(t('m-client:toast.success.client_created'));
    setCustomer({
      customerId: String(client._id),
      name: client.name,
      type: rolesToLegacyCustomerType(client.roles),
      phone: client.primaryContact?.phone ?? undefined,
    });
    setIsCreateOpen(false);
  };

  const lastSelectedId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (customer?.customerId && customer.customerId !== lastSelectedId.current) {
      lastSelectedId.current = customer.customerId;
      setSearchInput('');
    }
  }, [customer?.customerId]);

  const isPendingList = clientList.isLoading || clientList.isFetchingNextPage;

  const visibleRows = useMemo(() => {
    if (!customer?.customerId) return rows;
    const without = rows.filter(r => String(r._id) !== customer.customerId);
    const selected = rows.find(r => String(r._id) === customer.customerId);
    if (selected) return [selected, ...without];
    // The picked client wasn't in the current page (top-3 default,
    // or a client outside the search hits) — surface the explicit
    // fallback fetch so the chip still renders. Until the fallback
    // resolves we leave the existing rows alone so the list doesn't
    // flash.
    if (selectedFallback.data) return [selectedFallback.data, ...without];
    return rows;
  }, [rows, customer?.customerId, selectedFallback.data]);

  return (
    <Card className="p-4">
      <SectionHeader
        title={
          isInternalShipment
            ? t('wizard.customer.internal_title', { defaultValue: 'Internal shipment' })
            : t('wizard.customer.title', { defaultValue: 'Client' })
        }
        subtitle={
          isInternalShipment
            ? t('wizard.customer.internal_subtitle', {
                defaultValue: 'Moving your own goods — no external client.',
              })
            : t('wizard.customer.subtitle', { defaultValue: 'Who is this order for?' })
        }
        action={
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {t('wizard.customer.internal_toggle', { defaultValue: 'Internal shipment' })}
            </span>
            <Switch
              checked={isInternalShipment}
              onCheckedChange={next => setInternalShipment(next)}
              aria-label={t('wizard.customer.internal_toggle', {
                defaultValue: 'Internal shipment',
              })}
            />
          </div>
        }
      />

      {isInternalShipment ? (
        <div className="mt-3 flex items-start gap-3 rounded-md border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
          <div className="flex size-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 flex-shrink-0">
            <PackageIcon className="size-4" />
          </div>
          <div className="space-y-1">
            <div className="font-medium">
              {t('wizard.customer.internal_title', { defaultValue: 'Internal shipment' })}
            </div>
            <div className="text-blue-900/70">
              {t('wizard.customer.internal_hint', {
                defaultValue:
                  'This load belongs to your own organization. No external receiver will be linked.',
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <Input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={t('wizard.customer.search_placeholder', {
              defaultValue: 'Search by name…',
            })}
          />

          {isPendingList ? (
            <div className="px-3 py-6 text-center text-sm text-gray-400">
              {t('c-main:loading', { defaultValue: 'Loading…' })}
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-200 p-3 text-sm text-gray-500">
              {t('wizard.customer.empty', { defaultValue: 'No clients found.' })}
            </div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto pe-1">
              {visibleRows.map(c => {
                const id = String(c._id);
                const isSelected = customer?.customerId === id;
                const primaryRole = c.roles?.[0];
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handlePickClient(c)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2.5 text-start transition-colors flex items-center gap-3',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300',
                    )}>
                    <div className="flex size-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
                      <UserIcon className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{c.name}</div>
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">
                        {primaryRole ? t(`m-client:roles.${primaryRole}.title`) : ''}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-gray-400">
            <PlusIcon className="size-4" />
            {t('wizard.customer.create_new', { defaultValue: 'Create new client' })}
          </button>
        </div>
      )}

      <ClientEditModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onClientSaved={handleClientSaved}
        mode="create"
        defaultValues={{ roles: ['business_customer'] }}
      />
    </Card>
  );
}
